import { getApiUrl } from '../../redux/api';
import store from '../../redux/store';
import { login } from '../../redux/features/authSlice';
import { isValidJwtStructure } from './loginSignupSecurity';
import { isRecognizedLoginUserType, navigateAfterLogin } from './postLoginNavigation';
import { resolveAuthReturnTo } from './guestBrowseIntent';

export { releaseGoogleOneTapPrompt } from './tokenManager';

const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';
const NO_ACCOUNT_CODE = 'NO_ACCOUNT';

function googleOneTapLegacyBrowser(userAgent) {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  if (/Firefox/i.test(userAgent)) return true;
  if (/Safari/i.test(userAgent) && !/Chrome|Chromium|Edg\//i.test(userAgent)) return true;
  return false;
}

/** Desktop Chrome and Edge draw One Tap with FedCM. Safari and Firefox use the on-page card. */
export function googleOneTapUsesFedcm(userAgent = '') {
  return !googleOneTapLegacyBrowser(userAgent);
}

const GOOGLE_ONE_TAP_PHONE_UA = /Android|Mobile|iPhone|iPad|iPod/i;

/** Phones, tablets, narrow windows, and desktop Chrome/Edge use Millet's button.
 * Chrome's FedCM account chooser is pinned to the top-right and covers the header. */
export function googleOneTapUsesOwnedCard(userAgent = '', mobileLayout = false) {
  if (mobileLayout) return true;
  if (GOOGLE_ONE_TAP_PHONE_UA.test(userAgent)) return true;
  return /Chrome|Chromium|Edg\//i.test(userAgent) && googleOneTapUsesFedcm(userAgent);
}

/** Wide windows that are not phones sit the card under the two-row header. */
export function googleOneTapClearsDesktopHeader(userAgent = '', mobileLayout = false) {
  if (mobileLayout) return false;
  return !GOOGLE_ONE_TAP_PHONE_UA.test(userAgent);
}

export function googleOneTapReturnTo(location) {
  const guestReturn = resolveAuthReturnTo(null);
  if (guestReturn) return guestReturn;
  if (!location || location.pathname === '/' || location.pathname === '/login') return null;
  return resolveAuthReturnTo(location);
}

function googleIdentity() {
  return window.google?.accounts?.id || null;
}

export function loadGoogleIdentityScript() {
  const ready = googleIdentity();
  if (ready) return Promise.resolve(ready);

  return new Promise((resolve, reject) => {
    const fail = (script) => {
      script?.remove();
      reject(new Error('Google sign-in failed to load'));
    };

    const finish = (script) => {
      const id = googleIdentity();
      if (id) resolve(id);
      else fail(script);
    };

    const existing = document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID);
    if (existing) {
      const status = existing.getAttribute('data-google-identity-status');
      if (status === 'ready') {
        finish(existing);
        return;
      }
      if (status === 'error') {
        fail(existing);
        return;
      }
      existing.addEventListener('load', () => {
        existing.setAttribute('data-google-identity-status', 'ready');
        finish(existing);
      }, { once: true });
      existing.addEventListener('error', () => {
        existing.setAttribute('data-google-identity-status', 'error');
        fail(existing);
      }, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_IDENTITY_SCRIPT_ID;
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-google-identity-status', 'ready');
      finish(script);
    };
    script.onerror = () => {
      script.setAttribute('data-google-identity-status', 'error');
      fail(script);
    };
    document.head.appendChild(script);
  });
}

export async function completeGoogleOneTapLogin({ credential, navigate, returnTo }) {
  if (!isValidJwtStructure(credential)) return false;

  let response;
  try {
    response = await fetch(`${getApiUrl()}/api/auth/google/one-tap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });
  } catch {
    return false;
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    if (data?.code === NO_ACCOUNT_CODE) {
      navigate('/sign-up?error=No%20account%20found.%20Please%20sign%20up%20first.', { replace: true });
    }
    return false;
  }

  const token = data?.token;
  const userType = data?.user_type || data?.userType;
  if (!isValidJwtStructure(token) || !isRecognizedLoginUserType(userType)) return false;

  localStorage.setItem('token', token);
  window.dispatchEvent(new Event('userLoggedIn'));
  store.dispatch(login({ userType }));
  await navigateAfterLogin(navigate, userType, returnTo);
  return true;
}
